import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Slider } from "../ui/slider";
import { Switch } from "../ui/switch";
import { 
  RotateCcw, 
  Type, 
  Palette, 
  Layout,
  Ruler,
  Image as ImageIcon
} from "lucide-react";
import { BUSINESS_CARD_STANDARDS, GOOGLE_FONTS, DESIGN_RULES } from "../../constants/businessCardStandards";
import PhotoUploader from "./PhotoUploader";

const ProfessionalCardDesigner = ({ cardData, onCardUpdate, selectedTemplate }) => {
  const [orientation, setOrientation] = useState("horizontal");
  const [customPhoto, setCustomPhoto] = useState(null);
  const [fontSettings, setFontSettings] = useState({
    companyName: { font: 'Inter', size: 16, weight: '700', color: '#000000' },
    personalName: { font: 'Inter', size: 12, weight: '600', color: '#000000' },
    jobTitle: { font: 'Inter', size: 10, weight: '400', color: '#333333' },
    contactInfo: { font: 'Inter', size: 8, weight: '400', color: '#333333' }
  });

  // Load Google Fonts
  useEffect(() => {
    GOOGLE_FONTS.forEach(font => {
      if (!document.querySelector(`link[href="${font.url}"]`)) {
        const link = document.createElement('link');
        link.href = font.url;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
    });
  }, []);

  const handleOrientationChange = (newOrientation) => {
    setOrientation(newOrientation);
    onCardUpdate({
      ...cardData,
      orientation: newOrientation
    });
  };

  const handlePhotoSelect = (photoUrl) => {
    setCustomPhoto(photoUrl);
    onCardUpdate({
      ...cardData,
      customPhoto: photoUrl
    });
  };

  const handleRemovePhoto = () => {
    setCustomPhoto(null);
    onCardUpdate({
      ...cardData,
      customPhoto: null
    });
  };

  const handleFontChange = (element, property, value) => {
    const newFontSettings = {
      ...fontSettings,
      [element]: {
        ...fontSettings[element],
        [property]: value
      }
    };
    setFontSettings(newFontSettings);
    onCardUpdate({
      ...cardData,
      fontSettings: newFontSettings
    });
  };

  const getCurrentDimensions = () => {
    const dims = BUSINESS_CARD_STANDARDS.dimensions;
    return orientation === "horizontal" 
      ? { width: dims.width, height: dims.height }
      : { width: dims.height, height: dims.width };
  };

  const renderTypographyControls = (elementKey, elementName) => {
    const settings = fontSettings[elementKey];
    const standards = BUSINESS_CARD_STANDARDS.typography[elementKey];

    const fontFamily = GOOGLE_FONTS.find(f => f.name === settings.font)?.family || 'Inter';
    const min = standards?.minSize || 6;
    const max = standards?.maxSize || 24;
    const rec = standards?.recommendedSize || 10;

    return (
      <div key={elementKey} className="p-3 bg-gray-50 border border-gray-200 space-y-2">
        {/* Row 1 · Element name + live preview inline */}
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs font-bold uppercase tracking-wider text-gray-700">
            {elementName}
          </div>
          <div
            className="text-right truncate flex-1 ml-2 px-2 py-1 bg-white border"
            style={{
              fontFamily, fontSize: `${Math.max(11, settings.size * 0.75)}px`,
              fontWeight: settings.weight, color: settings.color, lineHeight: 1,
            }}
          >Preview</div>
        </div>

        {/* Row 2 · Font family + weight selects compact */}
        <div className="grid grid-cols-2 gap-2">
          <Select value={settings.font} onValueChange={(v) => handleFontChange(elementKey, 'font', v)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GOOGLE_FONTS.map((font) => (
                <SelectItem key={font.name} value={font.name}>
                  <span style={{ fontFamily: font.family }}>{font.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={settings.weight} onValueChange={(v) => handleFontChange(elementKey, 'weight', v)}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="300">Light · 300</SelectItem>
              <SelectItem value="400">Normal · 400</SelectItem>
              <SelectItem value="500">Medium · 500</SelectItem>
              <SelectItem value="600">Semibold · 600</SelectItem>
              <SelectItem value="700">Bold · 700</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Row 3 · Size slider + number input + color picker inline */}
        <div className="flex items-center gap-2">
          <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 w-10">Size</div>
          <input
            type="range"
            value={settings.size}
            onChange={(e) => handleFontChange(elementKey, 'size', parseInt(e.target.value))}
            min={min} max={max} step={1}
            className="flex-1"
            style={{ accentColor: '#01579b' }}
          />
          <input
            type="number"
            value={settings.size}
            onChange={(e) => handleFontChange(elementKey, 'size', parseInt(e.target.value) || rec)}
            min={min} max={max}
            className="w-12 h-7 text-xs text-center font-mono border border-gray-300"
          />
          <span className="text-[10px] font-mono text-gray-500 w-10">pt · {rec}</span>
          <input
            type="color"
            value={settings.color}
            onChange={(e) => handleFontChange(elementKey, 'color', e.target.value)}
            className="w-7 h-7 border border-gray-300 cursor-pointer"
            title={settings.color}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Card Orientation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Layout className="h-5 w-5 text-blue-600" />
            <span>Card Layout & Orientation</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant={orientation === "horizontal" ? "default" : "outline"}
                onClick={() => handleOrientationChange("horizontal")}
                className="h-20 flex flex-col items-center justify-center space-y-2"
              >
                <div className="w-12 h-8 bg-current opacity-20 rounded"></div>
                <span className="text-sm">Horizontal</span>
                <span className="text-xs opacity-70">3.625" × 2.125"</span>
              </Button>
              
              <Button
                variant={orientation === "vertical" ? "default" : "outline"}
                onClick={() => handleOrientationChange("vertical")}
                className="h-20 flex flex-col items-center justify-center space-y-2"
              >
                <div className="w-8 h-12 bg-current opacity-20 rounded"></div>
                <span className="text-sm">Vertical</span>
                <span className="text-xs opacity-70">2.125" × 3.625"</span>
              </Button>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-start space-x-3">
                <Ruler className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">USA Business Card Standard</p>
                  <p>Current size: {getCurrentDimensions().width}" × {getCurrentDimensions().height}"</p>
                  <p className="text-xs mt-1 opacity-75">
                    Includes 1/8" bleed and safe zone for professional printing
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Photo Upload */}
      <PhotoUploader
        onPhotoSelect={handlePhotoSelect}
        existingPhoto={customPhoto || cardData.customPhoto}
        onRemovePhoto={handleRemovePhoto}
      />

      {/* Typography Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Type className="h-5 w-5 text-green-600" />
            <span>Professional Typography</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-sm text-green-800">
                <p className="font-semibold mb-2">Typography Best Practices:</p>
                <ul className="space-y-1 text-xs">
                  <li>• Company name should be the largest and boldest text</li>
                  <li>• Maintain consistent font families (max 2 fonts)</li>
                  <li>• Ensure sufficient contrast for readability</li>
                  <li>• Use font sizes that are readable when printed</li>
                </ul>
              </div>
            </div>

            {renderTypographyControls('companyName', 'Company Name')}
            {renderTypographyControls('personalName', 'Personal Name')}
            {renderTypographyControls('jobTitle', 'Job Title')}
            {renderTypographyControls('contactInfo', 'Contact Information')}
          </div>
        </CardContent>
      </Card>

      {/* Design Guidelines */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Palette className="h-5 w-5 text-purple-600" />
            <span>Design Guidelines</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-900">Do's:</h4>
                <ul className="space-y-1 text-gray-600">
                  <li>• Use high contrast colors</li>
                  <li>• Keep text readable at small sizes</li>
                  <li>• Maintain consistent spacing</li>
                  <li>• Use professional fonts</li>
                  <li>• Include essential contact info only</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-900">Don'ts:</h4>
                <ul className="space-y-1 text-gray-600">
                  <li>• Overcrowd the design</li>
                  <li>• Use more than 2-3 fonts</li>
                  <li>• Place text too close to edges</li>
                  <li>• Use very light colors</li>
                  <li>• Include too much information</li>
                </ul>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="font-semibold text-purple-900 mb-2">Print Quality Tips:</h4>
              <div className="text-sm text-purple-800 space-y-1">
                <p>• All designs are created at 300 DPI for crisp printing</p>
                <p>• Colors may appear slightly different when printed</p>
                <p>• Text smaller than 7pt may be hard to read</p>
                <p>• Leave 1/8" margin from edges for safe printing</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfessionalCardDesigner;